import { React, useState, useEffect } from "react";
import { Route, Routes, Navigate, Outlet, useLocation } from "react-router-dom"
import Landing from "./pages/general/landing";
import Login from "./pages/auth/login";
import "../src/assets/styles.css"
import Register from "./pages/auth/register";
import LearnerHome from "./pages/learner/learner-home";
import Notifications from "./pages/general/notification-tab";
import Loader from "./components/ui/loader";
import CourseLearningPage from "./pages/learner/course-learning-page";
import Catalogue from "./pages/learner/catalogue";
// import CourseProgress from "./components/course/course-progress";
import Payment from "./components/course/payment";
import CoursePreview from "./pages/learner/course-preview";
import Settings from "./pages/general/settings-tab";
import Overview from "./pages/admin/admin-dashboard/overview";
import Cart from "./components/course/cart";
import ResetPassword from "./components/auth/reset-password";
import VerificationEmailTemplate from "./components/ui/email-template";
import GoogleAuthRedirect from "./components/auth/role-selection-modal";
import FAQ from "./pages/general/faq"
import { CartProvider } from "./contexts/cartContext";
import RegSuccess from "./components/auth/registration-success";
import CourseManagement from "./pages/admin/admin-dashboard/course-management";
import { WishlistProvider } from "./contexts/wishlistContext";
import ExamPage from "./components/exam/exam";
import SessionTracker from "./components/auth/session-tracker";
import CreateCourse from "./pages/admin/create-course";
import PendingApproval from "./components/ui/pending-approval";
import ViewCourseDetails from "./pages/admin/view-course";
import AuthSuccess from "./components/auth/authSuccess";
import EditFaqs from "./pages/admin/edit-faqs";
import { AuthProvider } from "./contexts/userContext";
import ComingSoon from "./components/misc/coming-soon";
import UnauthorizedPage from "./components/ui/unauthorized";
import UserManagement from "./pages/admin/admin-dashboard/user-management";
import MyLearningLayout from "./components/layout/my-learning-layout";
import EnrolledCourses from "./pages/learner/my-learning-page/enrolled-courses";
import ExamHistory from "./pages/learner/my-learning-page/exam-history";
import CourseProgress from "./pages/learner/my-learning-page/course-progress";
import Wishlist from "./pages/learner/my-learning-page/wishlist";
import Certificates from "./pages/learner/my-learning-page/certificates";

import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ErrorBoundary, ErrorPage } from './components/ui/error-boundary';
import RegFailed from "./components/auth/registration-failed";
import { useAuth } from "./contexts/userContext";
import TitleManager from "./components/ui/title-manager";
import LearnerLayout from "./components/layout/learner-layout";
import AdminLayout from "./components/layout/admin-layout";
import TermsOfService from "./pages/general/terms-of-service";
import PrivacyPolicy from "./pages/general/privacy-policy";
import ContactUs from './pages/general/contact-us';
import Account from "./pages/user/account";
import ExamAttempt from "./components/exam/exam-attempt";
import Footer from "./components/navigation/footer"; // <-- adjust path to wherever your Footer lives
import CertificateOfCompletion from "./components/course/certificate";
import CertificatePreview from "./components/course/certificate-preview";
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth", // Change to "auto" if you don't want the animation
    });
  }, [pathname]);

  return null;
}

// Routes (or route prefixes) that should NOT show the footer
const NO_FOOTER_PATHS = [
  "/login",
  "/register",
  "/reset-password",
  "/google-auth-redirect",
  "/authSuccess",
  "/reg-success",
  "/reg-failed",
  "/coming-soon",
  "/loader",
  "/mail",
  "/exam",            // also covers /exam-attempt/...
];

// Sticky-footer shell: content grows to fill the viewport, footer sits at the bottom
function SiteShell({ children }) {
  const { pathname } = useLocation();
  const showFooter = !NO_FOOTER_PATHS.some((p) => pathname.startsWith(p));

  return (
    <div className="d-flex flex-column" style={{ minHeight: "100dvh" }}>
      <div className="flex-grow-1">{children}</div>
      {showFooter && <Footer />}
    </div>
  );
}

function App() {

  //user_id prop to be passed to course management component to filter courses by instructor
  const currentUserId = useAuth()?.user?.user_id;

  //supress warnings in console: temporary-----
  const suppressedWarnings = [
    '[rc-collapse] `children` will be removed in next major version. Please use `items` instead.',
    'Warning: [antd: Tabs] `Tabs.TabPane` is deprecated. Please use `items` instead.',
    'Warning: [antd: Breadcrumb] `Breadcrumb.Item and Breadcrumb.Separator` is deprecated. Please use `items` instead.'
  ];

  const originalError = console.error;
  console.error = (...args) => {
    if (typeof args[0] === 'string' && suppressedWarnings.some(w => args[0].includes(w))) {
      return; // ignore 
    }
    originalError(...args);
  };
  //------------------------------------



  const API_URL = process.env.REACT_APP_API_URL;
  const queryClient = new QueryClient()

  const PrivateRoute = ({ allowedRoles = [], children }) => {
    const { user, loading, checkAuth } = useAuth();
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
      if (!user && !loading) {
        const timer = setTimeout(() => {
          // Retry auth check (handles race condition when cookie not yet ready)
          if (retryCount < 3) {
            checkAuth();
            setRetryCount((prev) => prev + 1);
          }
        }, 500);

        return () => clearTimeout(timer);
      }
    }, [user, loading, retryCount, checkAuth]);

    if (loading) return <Loader />;

    // Not logged in
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    // If specific roles are required and user's role not in the list
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }

    // Authenticated and authorized
    return children ? children : <Outlet />;
  };



  return (
    process.env.REACT_APP_MAINTENANCE_MODE === "true" ? (
      <ComingSoon />
    ) : (
      <ErrorBoundary> {/* Optional, but handles runtime crashes gracefully */}
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CartProvider>
              <TitleManager />
              <ScrollToTop />
              <SiteShell>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/gen-cert" element={
                    <CertificatePreview
                      name="Mapalo Mwila-Chanda"
                      course="Introduction to Equities Trading"
                      completedAt="2026-10-13"
                    />
                  } />
                  <Route path="/coming-soon" element={<ComingSoon />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route
                    path="/"
                    element={
                      process.env.REACT_APP_MAINTENANCE_MODE === "true"
                        ? <ComingSoon />
                        : <Landing />
                    }
                  />
                  <Route path="/landing" element={<Landing />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/mail" element={<VerificationEmailTemplate />} />
                  <Route path="/google-auth-redirect" element={<GoogleAuthRedirect />} />
                  <Route path="/faqs" element={<FAQ />} />
                  <Route path="/reg-success" element={<RegSuccess />} />
                  <Route path="/reg-failed" element={<RegFailed />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/pending-approval" element={<PendingApproval />} />
                  <Route path="/error" element={<ErrorPage />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/account/*" element={<Account />} />
                  <Route path="/loader" element={<Loader messages={['Preparing files', 'Uploading course content', 'Finalizing']} />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  {/* Admin Only Routes */}
                  <Route
                    element={
                      <PrivateRoute allowedRoles={["Admin", "Instructor"]} />
                    }
                  >
                    <Route path="/admin-dashboard" element={<AdminLayout />}>
                      <Route index element={<Navigate to="overview" replace />} />
                      <Route path="overview" element={<Overview />} />
                      <Route path="courses" element={<CourseManagement currentUserId={currentUserId} />} />
                      <Route path="create-course" element={<CreateCourse />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="view-course/:courseId/:cloudId" element={<ViewCourseDetails />} />

                    </Route>

                  </Route>
                  {/* does not require admin header */}
                  <Route path="/authSuccess" element={<AuthSuccess />} /> {/* moved into AuthProvider to accomodate useAuth() in component  */}


                  {/* Learner Only Routes */}
                  <Route
                    element={


                      <WishlistProvider>
                        <PrivateRoute allowedRoles={["Learner"]} />
                      </WishlistProvider>


                    }
                  >
                    <Route path="/" element={<LearnerLayout />}>
                      <Route path="/learner-home" element={<LearnerHome />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/loader" element={<Loader />} />
                      <Route path="/course-learning" element={<CourseLearningPage />} />
                      <Route path="/course/:courseId" element={<CourseLearningPage />} />
                      <Route path="/course/:courseId-:slug" element={<CourseLearningPage />} />
                      <Route path="/catalogue" element={<Catalogue />} />
                      <Route path="/payment" element={<Payment />} />
                      <Route path="/course-preview/:courseId" element={<CoursePreview />} />
                      <Route path="/course-preview/:courseId-:slug" element={<CoursePreview />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/exam-attempt/:attemptId" element={<ExamAttempt />} />
                      <Route path="/exam/:courseId/:examId" element={<ExamPage />} />

                      {/* my learning page embedded routes */}
                      <Route path="/my-learning" element={<MyLearningLayout />}>
                        <Route index element={<Navigate to="enrolled-courses" replace />} />
                        <Route path="enrolled-courses" element={<EnrolledCourses />} />
                        <Route path="wishlist" element={<Wishlist />} />
                        <Route path="course-progress" element={<CourseProgress />} />
                        <Route path="exam-history" element={<ExamHistory />} />
                        <Route path="certificates" element={<Certificates />} />
                      </Route>

                    </Route>

                  </Route>
                </Routes>
              </SiteShell>
            </CartProvider>
          </AuthProvider>

        </QueryClientProvider>
      </ErrorBoundary>
    )
  );
}

export default App;